import { Paper, Typography, Button, Box, Divider, Chip, Grid } from "@mui/material";
import { formatarValorMonetario } from "../../../../../../../helpers/masks";
import { DetalheSolicitacaoData } from "..";
import { useMemo } from "react";

export default function CardGerarOc({ data, onGerarOc }: CardGerarOcProps) {

    const todasOrdensEmitidas = useMemo(() => {
        return data.itens.every(item => item.status_cotacao === "OC Emitida");
    }, [data]);

    if (data.cotacoes.length === 0 || !data.cotacoes.some(cotacao => cotacao.respondido_em !== null)) return null;
    const ordensCompra = Array.isArray(data.ordens_compra) ? data.ordens_compra : [];

    function getStatusChip(status: string, ocCancelada: boolean) {
        if (ocCancelada) {
            return (
                <Chip
                    color="error"
                    size="small"
                    label="Cancelada"
                />
            );
        }

        if (status === "S") {
            return (
                <Chip
                    color="success"
                    size="small"
                    label="Emitida"
                />
            );
        }

        return (
            <Chip
                color="warning"
                size="small"
                label="Pendente"
            />
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 2, background: "#fff" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: "#222", mb: 1 }}>
                Ordens de compra relacionadas
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {ordensCompra.length === 0 ? (
                    <Box sx={{
                        border: "2px dashed #20753A",
                        borderRadius: "8px",
                        py: 5,
                        px: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 170,
                    }}>
                        <Typography sx={{
                            color: "#20753A",
                            fontWeight: 500,
                            mb: 2,
                            textAlign: "center",
                            fontSize: 16,
                        }}>
                            Nenhuma OC vinculada a essa solicitação<br />Deseja vincular?
                        </Typography>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onGerarOc}
                        >
                            Gerar OC
                        </Button>
                    </Box>
                ) : (
                    ordensCompra.map((oc, idx) => (
                        <Box
                            key={oc.codigo + String(idx)}
                            sx={{
                                mb: 1,
                                pb: 2,
                                borderBottom: idx < ordensCompra.length - 1 ? "1px solid #eee" : "none",
                                position: "relative"
                            }}
                        >
                            <Box sx={{ position: "absolute", right: 0, top: 0 }}>
                                {getStatusChip(oc.status, oc.oc_cancelada)}
                            </Box>
                            <Typography variant="caption" sx={{ color: "#666" }}>Código</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: "#222" }}>
                                {oc.codigo}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#666", mt: 1 }}>Fornecedor</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#222", mb: 1 }}>
                                {oc.fornecedor || "-"}
                            </Typography>
                            <Grid container spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" sx={{ color: "#666", mt: 1 }}>Emitido em</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#222" }}>
                                        {formatDate(oc.data_emissao)}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" sx={{ color: "#666", mt: 1 }}>Valor</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#222" }}>
                                        {formatarValorMonetario(oc.valor_total ?? 0)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    ))
                )}

                {ordensCompra.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onGerarOc}
                            disabled={todasOrdensEmitidas}
                        >
                            Gerar OC
                        </Button>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}

interface CardGerarOcProps {
    data: DetalheSolicitacaoData;
    onGerarOc?: () => void;
}

function formatDate(dateIso: string) {
    if (!dateIso) return "--";
    const parts = dateIso.split("T")[0].split("-");
    if (parts.length !== 3) return dateIso;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
}
